import { useState } from "react";
import { Search, AlertTriangle, CheckCircle, XCircle, Loader2, Lightbulb, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

interface SeoAnalysisResult {
  score: number;
  issues: {
    type: "error" | "warning" | "info";
    message: string;
    fix: string;
  }[];
  optimizations: string[];
  titleAnalysis: {
    length: number;
    hasKeyword: boolean;
    isOptimal: boolean;
  };
  descriptionAnalysis: {
    length: number;
    hasKeyword: boolean;
    isOptimal: boolean;
  };
}

export const SeoMetaAnalyzer = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [keyword, setKeyword] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<SeoAnalysisResult | null>(null);

  const loadSample = () => {
    setTitle("Best Email Marketing Tools 2025 - Complete Guide | IT Dukes");
    setDescription("Discover the top email marketing tools for 2025. Our comprehensive guide covers pricing, features, and expert recommendations to boost your campaigns.");
    setKeyword("email marketing tools");
    setResult(null);
  };

  const analyzeMetaTags = () => {
    if (!title.trim()) {
      toast.error("Please enter a meta title to analyze");
      return;
    }

    setIsAnalyzing(true);
    
    setTimeout(() => {
      const issues: SeoAnalysisResult["issues"] = [];
      const optimizations: string[] = [];
      
      // Title analysis
      const titleLength = title.length;
      const titleHasKeyword = keyword ? title.toLowerCase().includes(keyword.toLowerCase()) : true;
      const titleIsOptimal = titleLength >= 50 && titleLength <= 60;
      
      if (titleLength < 30) {
        issues.push({
          type: "error",
          message: `Title too short (${titleLength} chars). Recommended: 50-60 characters.`,
          fix: "Add more descriptive keywords to your title."
        });
      } else if (titleLength > 60) {
        issues.push({
          type: "warning",
          message: `Title may be truncated in SERPs (${titleLength} chars). Max: 60 characters.`,
          fix: "Shorten your title to prevent truncation in search results."
        });
      } else if (titleLength >= 50 && titleLength <= 60) {
        optimizations.push("✓ Title length is optimal for search engines");
      }

      if (keyword && !titleHasKeyword) {
        issues.push({
          type: "warning",
          message: "Primary keyword not found in title.",
          fix: "Include your target keyword naturally in the title, preferably near the beginning."
        });
      } else if (keyword && titleHasKeyword) {
        optimizations.push("✓ Primary keyword present in title");
      }

      // Description analysis
      const descLength = description.length;
      const descHasKeyword = keyword ? description.toLowerCase().includes(keyword.toLowerCase()) : true;
      const descIsOptimal = descLength >= 150 && descLength <= 160;

      if (descLength === 0) {
        issues.push({
          type: "error",
          message: "Meta description is missing.",
          fix: "Add a compelling meta description to improve click-through rates."
        });
      } else if (descLength < 120) {
        issues.push({
          type: "warning",
          message: `Description too short (${descLength} chars). Recommended: 150-160 characters.`,
          fix: "Expand your description with relevant details and a call-to-action."
        });
      } else if (descLength > 160) {
        issues.push({
          type: "warning",
          message: `Description may be truncated (${descLength} chars). Max: 160 characters.`,
          fix: "Shorten your description to prevent truncation in search results."
        });
      } else if (descIsOptimal) {
        optimizations.push("✓ Meta description length is optimal");
      }

      if (keyword && description && !descHasKeyword) {
        issues.push({
          type: "info",
          message: "Primary keyword not found in description.",
          fix: "Include your target keyword naturally in the meta description."
        });
      } else if (keyword && description && descHasKeyword) {
        optimizations.push("✓ Primary keyword present in description");
      }

      // Additional checks
      if (title.includes("|") || title.includes("-")) {
        optimizations.push("✓ Good use of separators for brand inclusion");
      }

      if (description.includes("!") || description.toLowerCase().includes("discover") || description.toLowerCase().includes("learn")) {
        optimizations.push("✓ Description includes action-oriented language");
      }

      // Calculate score
      let score = 100;
      issues.forEach(issue => {
        if (issue.type === "error") score -= 25;
        else if (issue.type === "warning") score -= 10;
        else score -= 5;
      });
      score = Math.max(0, score);

      setResult({
        score,
        issues,
        optimizations,
        titleAnalysis: {
          length: titleLength,
          hasKeyword: titleHasKeyword,
          isOptimal: titleIsOptimal
        },
        descriptionAnalysis: {
          length: descLength,
          hasKeyword: descHasKeyword,
          isOptimal: descIsOptimal
        }
      });
      
      setIsAnalyzing(false);
    }, 800);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-success";
    if (score >= 50) return "text-warning";
    return "text-destructive";
  };

  const getScoreBg = (score: number) => {
    if (score >= 80) return "bg-success/10 border-success/20";
    if (score >= 50) return "bg-warning/10 border-warning/20";
    return "bg-destructive/10 border-destructive/20";
  };

  return (
    <div className="section-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Search className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            SEO Meta Tag Analyzer
          </h3>
          <p className="text-sm text-muted-foreground">
            Optimize your page titles and descriptions for search engines
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-foreground">
              Target Keyword (optional)
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadSample}
              className="text-xs text-primary hover:text-primary/80"
            >
              Load Sample
            </Button>
          </div>
          <Input
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setResult(null);
            }}
            placeholder="e.g., email marketing tools"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Meta Title
          </label>
          <Input
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setResult(null);
            }}
            placeholder="Enter your page title..."
            maxLength={100}
          />
          <p className="text-xs text-muted-foreground mt-1 text-right">
            {title.length}/60 recommended
          </p>
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Meta Description
          </label>
          <Textarea
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setResult(null);
            }}
            placeholder="Enter your meta description..."
            className="min-h-[80px] resize-none"
            maxLength={300}
          />
          <p className="text-xs text-muted-foreground mt-1 text-right">
            {description.length}/160 recommended
          </p>
        </div>

        <Button
          onClick={analyzeMetaTags}
          disabled={isAnalyzing || !title.trim()}
          className="w-full"
        >
          {isAnalyzing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Search className="w-4 h-4 mr-2" />
              Analyze SEO
            </>
          )}
        </Button>

        {result && (
          <div className="space-y-4 pt-4 border-t border-border">
            {/* Score Card */}
            <div className={`rounded-xl border p-4 ${getScoreBg(result.score)}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className={`w-8 h-8 ${getScoreColor(result.score)}`} />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      SEO Score
                    </p>
                    <p className={`text-2xl font-bold ${getScoreColor(result.score)}`}>
                      {result.score}/100
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* SERP Preview */}
            <div className="bg-muted/50 rounded-xl p-4 border border-border">
              <h4 className="text-sm font-medium text-foreground mb-3">Google Preview</h4>
              <div className="bg-background rounded-lg p-3 border border-border">
                <p className="text-[#1a0dab] text-lg hover:underline cursor-pointer truncate">
                  {title || "Your Page Title Here"}
                </p>
                <p className="text-success text-sm">
                  https://example.com/your-page
                </p>
                <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                  {description || "Your meta description will appear here..."}
                </p>
              </div>
            </div>

            {/* Issues */}
            {result.issues.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-warning" />
                  Issues Found ({result.issues.length})
                </h4>
                <div className="space-y-2">
                  {result.issues.map((issue, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border ${
                        issue.type === "error" 
                          ? "bg-destructive/5 border-destructive/20" 
                          : issue.type === "warning"
                          ? "bg-warning/5 border-warning/20"
                          : "bg-primary/5 border-primary/20"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {issue.type === "error" ? (
                          <XCircle className="w-4 h-4 text-destructive mt-0.5" />
                        ) : issue.type === "warning" ? (
                          <AlertTriangle className="w-4 h-4 text-warning mt-0.5" />
                        ) : (
                          <Lightbulb className="w-4 h-4 text-primary mt-0.5" />
                        )}
                        <div>
                          <p className="text-sm text-foreground">{issue.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">{issue.fix}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Optimizations */}
            {result.optimizations.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-success" />
                  What's Working
                </h4>
                <ul className="space-y-1">
                  {result.optimizations.map((opt, index) => (
                    <li key={index} className="text-sm text-success flex items-center gap-2">
                      {opt}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
